import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PurchaseOrderList } from '../components/purchaseOrders/PurchaseOrderList';
import { PurchaseOrderForm } from '../components/purchaseOrders/PurchaseOrderForm';
import { PurchaseOrder, PurchaseOrderFilters } from '../types/purchaseOrder';

export const PurchaseOrdersPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | undefined>();
  const [expandedPOId, setExpandedPOId] = useState<string | null>(null);
  const [filters, setFilters] = useState<PurchaseOrderFilters>({
    page: 1,
    limit: 10,
    search: '',
    status: undefined,
    vendorId: undefined
  });

  // If ID is in URL, expand that PO
  useEffect(() => {
    if (id) {
      setExpandedPOId(id);
    } else {
      setExpandedPOId(null);
    }
  }, [id]);

  const handleCreatePO = () => {
    setSelectedPO(undefined);
    setShowForm(true);
  };

  const handleEditPO = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setShowForm(true);
  };

  const handleViewPO = (po: PurchaseOrder) => {
    // Toggle expanded state
    if (expandedPOId === po.id) {
      setExpandedPOId(null);
      navigate('/purchase-orders');
    } else {
      setExpandedPOId(po.id);
      navigate(`/purchase-orders/${po.id}`);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSelectedPO(undefined);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setSelectedPO(undefined);
  };

  const updateFilters = (newFilters: Partial<PurchaseOrderFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const inputStyle: React.CSSProperties = {
    padding: '9px 12px', background: '#f4efe2',
    border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
    fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
    outline: 'none', colorScheme: 'light' as any, boxSizing: 'border-box' as const,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header */}
      <div style={{ paddingBottom: 20, borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 400, letterSpacing: '0.4em', textTransform: 'uppercase', color: '#8a8778', marginBottom: 8 }}>
            Purchasing
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 300, color: '#1c2033', lineHeight: 1 }}>
            Purchase Orders
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 300, color: '#8a8778', marginTop: 8 }}>
            Manage purchase orders to vendors
          </div>
        </div>
        <button
          onClick={handleCreatePO}
          style={{
            fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 500,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            background: 'linear-gradient(135deg, #d97706, #b45309)',
            color: '#1c2033', border: 'none', padding: '12px 24px',
            borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 32px rgba(217,119,6,0.25)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none'; }}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Purchase Order
        </button>
      </div>

      {/* Filters */}
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search purchase orders..."
            value={filters.search || ''}
            onChange={(e) => updateFilters({ search: e.target.value })}
            style={{ ...inputStyle, flex: 1, minWidth: 200 }}
            onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(217,119,6,0.35)'; }}
            onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(92,78,51,0.25)'; }}
          />
          <select
            value={filters.status || ''}
            onChange={(e) => updateFilters({ status: e.target.value as any || undefined })}
            style={{ ...inputStyle, width: 180, cursor: 'pointer' }}
          >
            <option value="">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="ORDERED">Ordered</option>
            <option value="PARTIALLY_RECEIVED">Partially Received</option>
            <option value="RECEIVED">Received</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Purchase Order List */}
      <PurchaseOrderList
        filters={filters}
        onViewPO={handleViewPO}
        onEditPO={handleEditPO}
        expandedPOId={expandedPOId}
        setExpandedPOId={setExpandedPOId}
      />

      {/* Purchase Order Form Modal */}
      {showForm && (
        <PurchaseOrderForm
          purchaseOrder={selectedPO}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};
